import { MultimediaConstants } from './multimedia.constants';

/**
 * Multimedia validation and upload controller
 * Extend via Inherit pattern: class MyController extends Inherit(BaseClass, MultimediaController)
 * Centralizes file validation logic for support tickets across all components
 */
class MultimediaController {
    /**
     * Validate ticket attachments against size and count limits
     * @param {Array} attachments - Array of attachment objects with { file, name, type }
     * @returns {{ ok: boolean, error?: string, validFiles?: Array }}
     */
    validateAttachments(attachments) {
        if (!Array.isArray(attachments)) {
            return { ok: true, validFiles: [] };
        }

        const MAX_FILE_COUNT = MultimediaConstants.MAX_FILE_COUNT;
        const MAX_FILE_SIZE_MB = MultimediaConstants.MAX_FILE_SIZE_MB;
        const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

        const validFiles = [];

        for (const att of attachments) {
            // Skip non-File objects (e.g., already-uploaded URLs)
            if (!(att.file instanceof File)) continue;

            // Check file count limit
            if (validFiles.length >= MAX_FILE_COUNT) {
                return {
                    ok: false,
                    error: `Maximum ${MAX_FILE_COUNT} files allowed per ticket.`
                };
            }

            // Check file size limit
            if (att.file.size > MAX_FILE_SIZE_BYTES) {
                const sizeMB = (att.file.size / (1024 * 1024)).toFixed(1);
                return {
                    ok: false,
                    error: `File "${att.name}" is ${sizeMB}MB. Maximum size is ${MAX_FILE_SIZE_MB}MB.`
                };
            }

            validFiles.push(att);
        }

        return { ok: true, validFiles };
    }

    /**
     * Upload attachments to S3 with full orchestration
     * Validates, requests presigned URLs, uploads files in parallel, and returns URLs.
     * If any upload fails, throws error with details on which files failed.
     * Note: Partial upload failure may leave orphaned S3 objects; server-side cleanup is required.
     * @param {Array} attachments - Array of { file, name, type }
     * @param {Object} handlers - { requestPresigned, uploadToS3 } functions from DataController
     * @returns {Promise<Array>} Array of { url, name, type, size_bytes }
     * @throws {Error} Upload validation error or detailed upload failure with file info
     */
    async uploadAttachments(attachments, handlers) {
        const { requestPresigned, uploadToS3 } = handlers;

        // Validate before any API calls
        const validation = this.validateAttachments(attachments);
        if (!validation.ok) {
            throw new Error(validation.error);
        }

        if (validation.validFiles.length === 0) {
            return [];
        }

        try {
            // Request all presigned URLs upfront
            const presignedRequests = await Promise.all(
                validation.validFiles.map(att =>
                    requestPresigned(att.name, att.type)
                        .then(presigned => ({ ...att, presigned }))
                        .catch(err => {
                            throw new Error(`Failed to get upload URL for "${att.name}": ${err.message}`);
                        })
                )
            );

            // Upload all files in parallel with tracking
            const uploadResults = await Promise.allSettled(
                presignedRequests.map(item =>
                    uploadToS3(item.presigned.presignedUrl, item.file, item.type)
                        .then(() => ({
                            status: 'success',
                            url: item.presigned.publicUrl,
                            name: item.name,
                            type: item.type,
                            size_bytes: item.file.size || 0
                        }))
                )
            );

            // Check for failures
            const failed = uploadResults
                .map((result, idx) => ({
                    ...result,
                    fileName: presignedRequests[idx].name,
                    presigned: presignedRequests[idx].presigned
                }))
                .filter(result => result.status === 'rejected');

            if (failed.length > 0) {
                const failedNames = failed.map(f => f.fileName).join(', ');
                const error = new Error(
                    `Failed to upload ${failed.length} file(s): ${failedNames}. ` +
                    `Contact support if orphaned files persist.`
                );
                error.code = 'UPLOAD_PARTIAL_FAILURE';
                error.uploadResults = uploadResults;
                error.presignedRequests = presignedRequests;
                error.failedFiles = failed;
                throw error;
            }

            // Return successfully uploaded files
            return uploadResults
                .filter(result => result.status === 'fulfilled')
                .map(result => result.value);
        } catch (error) {
            // Re-throw with additional context if not already enhanced
            if (!error.code) {
                error.code = 'UPLOAD_FAILED';
            }
            throw error;
        }
    }
}

export default MultimediaController;
